import React, { useRef, useEffect, useState, useCallback } from 'react';
import FadeIn from '../shared/FadeIn';
import ScrollRevealText from '../shared/ScrollRevealText';
import CTASection from '../shared/CTASection';
import FAQAccordion from '../shared/FAQAccordion';
import { getProductFeatures } from '../../data/productFeatures';
import { getHomeFaqs } from '../../data/faqData';
import { useLang, useT } from '../../i18n/LanguageContext';
import strings from './FeaturePage.i18n';
import './FeaturePage.css';
import './HomePage.css';
import './PricingPage.css';

export default function FeaturePage({ feature, onNavigate }) {
  const activeTabRef = useRef(null);
  const gridRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const { lang } = useLang();
  const t = useT(strings);
  const productFeatures = getProductFeatures(lang);
  const homeFaqs = getHomeFaqs(lang);

  useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [feature?.id]);

  const handleGridScroll = useCallback(() => {
    const el = gridRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setScrollProgress(maxScroll > 0 ? el.scrollLeft / maxScroll : 0);
  }, []);

  if (!feature) return null;

  const handleRequestDemo = () => {
    window.open(
      'https://wa.me/601163688943?text=Hi%2C%20I%20would%20like%20to%20get%20started%20with%20SAPOT%20AI',
      '_blank'
    );
  };

  return (
    <div className="feature-page">
      {/* Hero */}
      <section className="feature-page__hero gradient-hero">
        <div className="section" style={{ textAlign: 'center' }}>
          <FadeIn>
            <span className="feature-page__icon">{feature.icon}</span>
          </FadeIn>
          <FadeIn delay={100}>
            <h1 className="feature-page__title">{feature.title}</h1>
          </FadeIn>
          <FadeIn delay={200}>
            <ScrollRevealText className="feature-page__subtitle">
              {feature.shortDesc}
            </ScrollRevealText>
          </FadeIn>
        </div>
      </section>

      {/* Feature Tab Bar */}
      <div className="feature-tab-bar">
        <div className="feature-tab-bar__track">
          {productFeatures.map((f) => (
            <button
              key={f.id}
              ref={f.id === feature.id ? activeTabRef : null}
              className={`feature-tab-bar__tab${f.id === feature.id ? ' feature-tab-bar__tab--active' : ''}`}
              onClick={() => onNavigate && onNavigate('feature-' + f.id)}
            >
              <span className="feature-tab-bar__icon">{f.icon}</span>
              <span className="feature-tab-bar__label">{f.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Feature Sections */}
      {feature.sections.map((section, idx) => (
        <FadeIn key={idx}>
          <section className={`feature-page__section ${idx % 2 !== 0 ? 'feature-page__section--reverse' : ''}`}>
            <div className="section">
              <div className="feature-page__row">
                <div className="feature-page__text">
                  <h2 className="feature-page__section-title">{section.title}</h2>
                  <ScrollRevealText className="feature-page__section-desc">
                    {section.description}
                  </ScrollRevealText>
                </div>
                <div className="feature-page__image">
                  <div className="screenshot-container">
                    <img src={section.screenshot} alt={section.title} />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </FadeIn>
      ))}

      {/* One platform — All Features Grid */}
      <section className="home-products feature-page__products">
        <div className="home-products__container">
          <div className="home-products__float-img">
            <img src={process.env.PUBLIC_URL + '/images/All features.png'} alt={t.featuresImageAlt} />
          </div>
          <div className="home-products__header">
            <p className="section-label">{t.allFeaturesLabel}</p>
            <h2 className="section-title">
              {t.allFeaturesTitle}
            </h2>
            <button className="btn-accent home-products__cta" onClick={handleRequestDemo}>
              {t.allFeaturesCta}
            </button>
          </div>
          <div className="home-products__grid" ref={gridRef} onScroll={handleGridScroll}>
            {productFeatures.map((f, i) => (
              <FadeIn key={f.id} delay={i * 20} threshold={0.01}>
                <div
                  className="home-product-card"
                  onClick={() => onNavigate && onNavigate('feature-' + f.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onNavigate && onNavigate('feature-' + f.id);
                    }
                  }}
                >
                  <div className="home-product-card__icon" style={{ background: f.iconBg }}>
                    {f.icon}
                  </div>
                  <h3 className="home-product-card__title">{f.title}</h3>
                  <p className="home-product-card__desc">{f.shortDesc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <div className="home-products__progress">
            <div
              className="home-products__progress-fill"
              style={{ transform: `scaleX(${scrollProgress})` }}
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pricing-faq">
        <div className="pricing-faq__inner">
          <div className="pricing-faq__grid">
            <div className="pricing-faq__left">
              <picture>
                <source
                  media="(max-width: 768px)"
                  srcSet={process.env.PUBLIC_URL + '/images/faq%20mobile.png'}
                />
                <img
                  src={process.env.PUBLIC_URL + '/images/faq flow.png'}
                  alt={t.faqImageAlt}
                  className="pricing-faq__image"
                />
              </picture>
            </div>
            <div className="pricing-faq__right">
              <span className="pricing-faq__label">{t.faqLabel}</span>
              <h2 className="pricing-faq__title">{t.faqTitle}</h2>
              <FAQAccordion items={homeFaqs} />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <FadeIn>
        <CTASection
          title={t.ctaTitle}
          subtitle={t.ctaSubtitle}
          buttonText={t.ctaButton}
        />
      </FadeIn>
    </div>
  );
}
